import objectToURLSearchParams from '@lib/util/object-to-url-search-params';
import {
    buildApiResult,
    fetchWithTimeout,
    FetchWithTimeoutOptions,
    genericNetworkError,
    handleHttpErrors,
} from './api-util';
import { fetch_timeout_duration } from '@lib/constants/network-constants';
import { getLogger } from '@lib/util/logging-util';

/**
 * Generates a CURL command equivalent to the fetch request for debugging purposes
 */
function generateCurlCommand(url: string, options: RequestInit): string {
    const method = options.method || 'GET';
    let curl = `curl -X ${method}`;

    // Add headers
    if (options.headers) {
        const headers = options.headers as Headers;
        headers.forEach((value, key) => {
            // Mask sensitive headers
            const maskedValue = key.toLowerCase().includes('key') ||
                               key.toLowerCase().includes('token') ||
                               key.toLowerCase().includes('authorization')
                ? '***MASKED***'
                : value;
            curl += ` \\\n  -H "${key}: ${maskedValue}"`;
        });
    }

    // Add body if present
    if (options.body) {
        const bodyStr = typeof options.body === 'string'
            ? options.body
            : JSON.stringify(options.body);
        // Escape single quotes in the body for shell
        const escapedBody = bodyStr.replace(/'/g, "'\\''");
        curl += ` \\\n  -d '${escapedBody}'`;
    }

    // Add URL (quoted to handle special characters)
    curl += ` \\\n  "${url}"`;

    return curl;
}

/**
 * USIO API client for prepaid card integration
 * Note: For runtime configuration in client components, use useApi().config.USIO_APP_URL
 * This function uses build-time env vars as fallback for USIO prepaid card API.
 */
export default async function usioApi(
    method: string,
    path: string = '',
    payload?: {
        query?: Record<string, any>;
        body?: Record<string, any>;
    },
    access_token?: string,
    header_username?: string
) {
    const requestId = `usio-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const logger = getLogger();

    logger.verbose(`[usioApi][${requestId}] ========== START ==========`);
    logger.verbose(`[usioApi][${requestId}] Timestamp: ${new Date().toISOString()}`);
    logger.verbose(`[usioApi][${requestId}] Method: ${method}`);
    logger.verbose(`[usioApi][${requestId}] Path (original): ${path}`);
    logger.verbose(`[usioApi][${requestId}] Has access token: ${!!access_token}`);
    logger.verbose(`[usioApi][${requestId}] Header username: ${header_username || '(none)'}`);


    const headers = new Headers();
    headers.append('Authorization', `Bearer ${access_token}`);
    if (method === 'GET') {
        if (header_username)
            headers.append(
                'akimbo-impersonate-as-username',
                header_username || ''
            );
    } else {
        headers.append('Content-Type', 'application/x-www-form-urlencoded');
    }

    const options: RequestInit = {
        method,
        headers: headers,
        cache: 'no-store',
    };

    if (payload?.body) {
        options.body = objectToURLSearchParams(payload.body);
    }

    if (payload?.query) {
        const params = objectToURLSearchParams(payload.query).toString();
        path = `${path}?${params}`;
    }

    try {
        const rootUrl = process.env.USIO_APP_URL
            ? process.env.USIO_APP_URL
            : 'https://app.akimbocard.com/api/v2';

        const fullUrl = `${rootUrl}${path}`;

        logger.verbose(`[usioApi][${requestId}] ========== REQUEST ==========`);
        logger.verbose(`[usioApi][${requestId}] Full URL: ${fullUrl}`);
        logger.verbose(`[usioApi][${requestId}] Root URL: ${rootUrl}`);
        logger.verbose(`[usioApi][${requestId}] Final path: ${path}`);
        logger.verbose(`[usioApi][${requestId}] Timeout: ${fetch_timeout_duration}ms`);

        const fetchWithTimeoutOptions: FetchWithTimeoutOptions = {
            timeout: fetch_timeout_duration,
            enableInterceptor: true,
            interceptorServiceName: 'usio-api',
            ...options,
        };

        // Generate CURL command for debugging (will be logged only on error)
        const curlCommand = generateCurlCommand(fullUrl, options);

        const startTime = Date.now();

        logger.verbose(`[usioApi][${requestId}] Sending request at ${new Date(startTime).toISOString()}...`);

        const response = await fetchWithTimeout(
            fullUrl,
            fetchWithTimeoutOptions
        );

        const duration = Date.now() - startTime;

        logger.verbose(`[usioApi][${requestId}] Response received in ${duration}ms`);
        logger.verbose(`[usioApi][${requestId}] ========== RESPONSE ==========`);
        logger.verbose(`[usioApi][${requestId}] Status: ${response?.status} ${response?.statusText}`);
        logger.verbose(`[usioApi][${requestId}] OK: ${response?.ok}`);

        if (!response?.ok) {
            logger.error(`[usioApi][${requestId}] ========== ERROR RESPONSE ==========`);
            logger.error(`[usioApi][${requestId}] Status: ${response?.status} ${response?.statusText}`);
            logger.error(`[usioApi][${requestId}] URL: ${fullUrl}`);
            logger.error(`[usioApi][${requestId}] Duration: ${duration}ms`);
            logger.error(`[usioApi][${requestId}] ========== CURL TO REPRODUCE ==========`);
            logger.error(curlCommand);
            logger.error(`[usioApi][${requestId}] ====================================`);

            try {
                await handleHttpErrors(response);
            } catch (error) {
                logger.error(`[usioApi][${requestId}] handleHttpErrors caught:`, error);
            }
        }

        let body;
        try {
            const contentType = response?.headers.get('content-type');

            logger.verbose(`[usioApi][${requestId}] Content-Type: ${contentType}`);

            if (contentType?.includes('application/json')) {
                const responseText = await response?.text();

                logger.verbose(`[usioApi][${requestId}] Response body length: ${responseText?.length || 0} characters`);

                try {
                    body = JSON.parse(responseText);
                    logger.verbose(`[usioApi][${requestId}] Parsed JSON successfully`);
                } catch (parseError) {
                    logger.error(`[usioApi][${requestId}] Failed to parse JSON:`, parseError);
                    logger.error(`[usioApi][${requestId}] Raw text:`, responseText);
                    logger.error(`[usioApi][${requestId}] ========== CURL TO REPRODUCE ==========`);
                    logger.error(curlCommand);
                    logger.error(`[usioApi][${requestId}] ====================================`);
                    throw new Error(`Failed to parse JSON response`);
                }
            } else {
                const text = await response?.text();
                logger.error(`[usioApi][${requestId}] ========== UNEXPECTED CONTENT TYPE ==========`);
                logger.error(`[usioApi][${requestId}] URL: ${fullUrl}`);
                logger.error(`[usioApi][${requestId}] Status: ${response?.status} ${response?.statusText}`);
                logger.error(`[usioApi][${requestId}] Content-Type: ${contentType}`);
                logger.error(`[usioApi][${requestId}] Expected: application/json`);
                logger.error(`[usioApi][${requestId}] Response length: ${text?.length || 0} characters`);
                logger.error(`[usioApi][${requestId}] Response body (full):`, text);
                logger.error(`[usioApi][${requestId}] ========== CURL TO REPRODUCE ==========`);
                logger.error(curlCommand);
                logger.error(`[usioApi][${requestId}] ====================================`);
                throw new Error(`Failed to parse JSON response`);
            }
        } catch (ex) {
            logger.error(`[usioApi][${requestId}] ========== PARSE ERROR ==========`);
            logger.error(`[usioApi][${requestId}] Error:`, ex);
            logger.error(`[usioApi][${requestId}] ========== CURL TO REPRODUCE ==========`);
            logger.error(curlCommand);
            logger.error(`[usioApi][${requestId}] ====================================`);
            throw ex;
        }

        logger.verbose(`[usioApi][${requestId}] ========== SUCCESS ==========`);
        logger.verbose(`[usioApi][${requestId}] Total duration: ${duration}ms`);
        logger.verbose(`[usioApi][${requestId}] ========== END ==========`);

        return buildApiResult(response, body);
    } catch (error: any) {
        logger.error(`[usioApi][${requestId}] ========== EXCEPTION ==========`);
        logger.error(`[usioApi][${requestId}] Error type: ${error?.constructor?.name}`);
        logger.error(`[usioApi][${requestId}] Error message: ${error?.message}`);
        logger.error(`[usioApi][${requestId}] Error stack:`, error?.stack);
        logger.error(`[usioApi][${requestId}] Full error:`, JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
        logger.error(`[usioApi][${requestId}] ========== END ==========`);
        throw genericNetworkError;
    }
}
